import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { Severity, FindingStatus } from '@nirvik/shared';

const severityOptions: { value: Severity; label: string; color: string; activeColor: string }[] = [
  { value: 'critical', label: 'Critical', color: 'border-gray-300 dark:border-border text-muted-foreground dark:text-foreground', activeColor: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 border-red-200 dark:border-red-500' },
  { value: 'high', label: 'High', color: 'border-gray-300 dark:border-border text-muted-foreground dark:text-foreground', activeColor: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-500' },
  { value: 'medium', label: 'Medium', color: 'border-gray-300 dark:border-border text-muted-foreground dark:text-foreground', activeColor: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-500' },
  { value: 'low', label: 'Low', color: 'border-gray-300 dark:border-border text-muted-foreground dark:text-foreground', activeColor: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-500' },
  { value: 'info', label: 'Info', color: 'border-gray-300 dark:border-border text-muted-foreground dark:text-foreground', activeColor: 'bg-gray-200 dark:bg-muted text-gray-700 dark:text-foreground border-gray-300 dark:border-border' },
];

const statusOptions: { value: FindingStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'fix-merged', label: 'Fix merged' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'dismissed', label: 'Dismissed' },
];

interface FindingFiltersProps {
  selectedSeverities: Severity[];
  onSeverityToggle: (severity: Severity) => void;
  selectedStatuses: FindingStatus[];
  onStatusToggle: (status: FindingStatus) => void;
  resultCount: number;
}

export function FindingFilters({
  selectedSeverities,
  onSeverityToggle,
  selectedStatuses,
  onStatusToggle,
  resultCount,
}: FindingFiltersProps) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4">
        {/* Severity filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground dark:text-muted-foreground mr-1">Severity:</span>
          {severityOptions.map((opt) => {
            const active = selectedSeverities.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onSeverityToggle(opt.value)}
                className="focus:outline-none"
              >
                <Badge
                  variant="outline"
                  className={cn(
                    'cursor-pointer transition-colors font-medium',
                    active ? opt.activeColor : opt.color
                  )}
                >
                  {opt.label}
                </Badge>
              </button>
            );
          })}
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground dark:text-muted-foreground mr-1">Status:</span>
          {statusOptions.map((opt) => {
            const active = selectedStatuses.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onStatusToggle(opt.value)}
                className="focus:outline-none"
              >
                <Badge
                  variant="outline"
                  className={cn(
                    'cursor-pointer transition-colors font-medium',
                    active
                      ? 'bg-card dark:bg-gray-100 text-white dark:text-gray-900 border-border dark:border-gray-100'
                      : 'border-gray-200 dark:border-border text-muted-foreground dark:text-muted-foreground'
                  )}
                >
                  {opt.label}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-muted-foreground dark:text-muted-foreground">
        Showing {resultCount} finding{resultCount !== 1 ? 's' : ''}
      </p>
    </div>
  );
}
